import { computed, makeObservable, observable } from "mobx";

import { BlockData, BlockModel, BlockRemoteData } from "@courses-models/course/src/ts/models/BlockModel";

/**
 * Specification of raw data required to create a new YoutubeBlockModel.
 */
type YoutubeBlockData = BlockData & {
    videoUrl: string;
};

/**
 * Specification of the format used when serializing youtube block content for
 * storage in the remote backend.
 */
type YoutubeBlockRemoteData = {
    videoUrl: string;
};

/**
 * Youtube blocks basically contain an id of a YouTube video, which should be
 * embedded inside the course´.
 */
export class YoutubeBlockModel extends BlockModel {
    /**
     * Specifies the url to the video on Youtube (copy-pasted), which we're
     * going to show inside this block.
     */
    public videoUrl: string;

    /**
     * Apply initial data when the model is instantiated.
     */
    constructor(initial: YoutubeBlockData) {
        super(initial);

        this.videoUrl = initial.videoUrl;

        makeObservable(this, {
            title: observable,
            note: observable,
            videoId: computed,
            videoUrl: observable,
        });
    }

    /**
     * Helper that'll create an instance of the LinkBlockModel based on a
     * serialized data from the remote backend.
     */
    public static DESERIALIZE(data: BlockRemoteData): YoutubeBlockModel {
        const content = <YoutubeBlockRemoteData>JSON.parse(data.content);

        return new YoutubeBlockModel({
            id: data.id,
            note: {
                content: data.note.content,
                enabled: data.note.isVisible,
            },
            title: data.title,
            ...content,
        });
    }

    /**
     * Extracts the video id from the video url defined above
     */
    public get videoId(): string | undefined {
        // Handle youtube.com (ie. direct links), where we extract id from the
        // ?v= parameter
        if (this.videoUrl.indexOf("youtube.com") !== -1 && this.videoUrl.indexOf("?v=") !== -1) {
            const videoId = this.videoUrl
                .split("?v=")[1]
                .split("&")[0]
                .split("#")[0];

            return videoId || undefined;
        }

        // Handle youtu.be links, where we extract id as the first folder of the
        // uri path
        if (this.videoUrl.indexOf("youtu.be/") !== -1) {
            const videoId = this.videoUrl
                .split("youtu.be/")[1]
                .split("&")[0]
                .split("#")[0];

            return videoId || undefined;
        }

        // If we get here, the link isn't recognized
        return undefined;
    }

    /**
     * Helper that'll return a serialized version of the content, so that it can
     * be stored remotely.
     */
    public serializeContent(): string {
        return JSON.stringify({ videoUrl: this.videoUrl });
    }

    /**
     * Helper that'll generate a snapshot of the content within this block
     */
    protected getContentSnapshot(): string {
        return this.videoUrl;
    }
}
